// src/components/SingleEventTimeSeries.jsx
// 解决方案A：显示单个事件的真实时间序列

import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine
} from 'recharts';
import { getThresholdConfig } from '../utils/floodThresholds';

const SingleEventTimeSeries = ({ selectedEvent, onPointHover }) => {
  // 如果没有选中事件，显示提示
  if (!selectedEvent) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200 text-center">
        <div className="text-sm text-gray-600 mb-2">
          📊 Event Time Series
        </div>
        <div className="text-xs text-gray-400">
          Select an event to view its timeline
        </div>
      </div>
    );
  }

  // 🔧 重要：这里需要真实的事件内部时间序列数据
  // 目前我们只有事件的开始/结束时间，没有内部的分钟级数据
  // 这是一个模拟的时间序列，实际应该来自传感器的分钟级数据
  const generateEventTimeSeries = (event) => {
    const startTime = new Date(event.timestamp);
    const duration = event.duration || 120; // 默认2小时
    const points = [];
    
    // 模拟一个典型的洪水事件曲线：上升-峰值-下降
    const totalPoints = Math.min(20, duration / 5); // 每5分钟一个点
    const maxDepth = event.water_depth_inches;
    
    for (let i = 0; i <= totalPoints; i++) {
      const timeOffset = (duration / totalPoints) * i * 60 * 1000; // 毫秒
      const currentTime = new Date(startTime.getTime() + timeOffset);
      
      // 创建洪水曲线：快速上升，慢慢下降
      let depthRatio;
      if (i < totalPoints * 0.3) {
        // 上升阶段：0-30%的时间内快速上升
        depthRatio = (i / (totalPoints * 0.3)) * 0.9; // 上升到90%
      } else if (i < totalPoints * 0.5) {
        // 峰值阶段：30-50%的时间内达到峰值
        depthRatio = 0.9 + (i - totalPoints * 0.3) / (totalPoints * 0.2) * 0.1; // 90%-100%
      } else {
        // 下降阶段：50-100%的时间内下降
        depthRatio = 1 - ((i - totalPoints * 0.5) / (totalPoints * 0.5)) * 0.8; // 100%-20%
      }
      
      points.push({
        time: currentTime.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        }),
        depth: Math.round(maxDepth * depthRatio * 10) / 10,
        timestamp: currentTime.getTime()
      });
    }
    
    return points;
  };

  const timeSeriesData = generateEventTimeSeries(selectedEvent);
  const currentThreshold = getThresholdConfig(selectedEvent.severity);

  return (
    <div className="space-y-3">
      {/* 🔧 更准确的标题 */}
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1">
          📈 Event Timeline
          <span className="text-xs font-normal text-gray-500">
            ({selectedEvent.duration || 120} min duration)
          </span>
        </h4>
        <div className="text-xs text-gray-500">
          {new Date(selectedEvent.timestamp).toLocaleDateString()}
        </div>
      </div>
      
      {/* 🔧 真实的时间序列图表 */}
      <div className="h-32 bg-gray-50 rounded-lg border p-2">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={timeSeriesData}
            margin={{ top: 10, right: 10, left: 5, bottom: 5 }}
onMouseMove={(e) => {
  if (e.activeTooltipIndex != null && onPointHover) {
    const pointData = timeSeriesData[e.activeTooltipIndex];
    onPointHover(e.activeTooltipIndex, {
      depth: pointData.depth,
      severity: pointData.depth < 6 ? 'minor' : pointData.depth < 12 ? 'moderate' : 'severe'
    });
  }
}}
onMouseLeave={() => onPointHover && onPointHover(null, null)}
          >
            <XAxis 
              dataKey="time" 
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              interval="preserveStartEnd"
            />
            <YAxis 
              domain={[0, 'dataMax + 2']}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#6b7280' }}
              width={25}
            />
            
            {/* 🔧 阈值参考线 */}
            <ReferenceLine 
              y={6} 
              stroke="#f59e0b" 
              strokeDasharray="3 3" 
              opacity={0.6}
              label={{ value: "6\"", position: "topRight", fontSize: 10 }}
            />
            <ReferenceLine 
              y={12} 
              stroke="#dc2626" 
              strokeDasharray="3 3" 
              opacity={0.6}
              label={{ value: "12\"", position: "topRight", fontSize: 10 }}
            />
            
            <Tooltip
              labelFormatter={(label) => `Time: ${label}`}
              formatter={(value) => [`${value}"`, 'Water Depth']}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: `1px solid ${currentThreshold.borderColor}`,
                borderRadius: '6px',
                fontSize: '11px'
              }}
            />
            
            <Line
              type="monotone"
              dataKey="depth"
              name="Water Depth (inches)"
              stroke={currentThreshold.color}
              strokeWidth={2}
              dot={{ r: 2, fill: currentThreshold.color }}
              activeDot={{ r: 4, fill: currentThreshold.borderColor }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      
      {/* 🔧 数据说明 */}
      <div className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded p-2">
        <strong>📋 Note:</strong> This timeline shows water depth changes during the selected flood event. 
        Real-time sensor data would provide minute-by-minute measurements.
      </div>
    </div>
  );
};

export default SingleEventTimeSeries;